import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function VenueSettings() {
    const { venue, role } = useOutletContext();
    const canToggle = role === "manager" || role === "admin";
    const [digestEnabled, setDigestEnabled] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!canToggle) return;
        endpoints.venueDigestPref(venue.id)
            .then((d) => setDigestEnabled(!!d.enabled))
            .catch(() => setDigestEnabled(null));
    }, [venue.id, canToggle]);

    const toggle = async () => {
        if (saving || digestEnabled === null) return;
        const next = !digestEnabled;
        setSaving(true);
        try {
            await endpoints.venueUpdateDigestPref(venue.id, next);
            setDigestEnabled(next);
            toast.success(next ? "Weekly digest turned on" : "Weekly digest turned off");
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <div className="pm-fade" data-testid="venue-settings">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Settings</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venue settings</h1>
            </header>
            <div className="px-4 md:px-8 py-6 max-w-2xl space-y-4">
                <div className="border border-[#2a2a2a] p-4 space-y-2">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Venue ID</div>
                    <div className="font-mono text-xs break-all" data-testid="venue-settings-id">{venue.id}</div>
                    <div className="text-[10px] font-mono uppercase text-[#666] mt-3">Your role</div>
                    <div className="font-mono text-xs">{role}</div>
                    <div className="text-[10px] font-mono uppercase text-[#666] mt-3">Account status</div>
                    <div className="font-mono text-xs" data-testid="venue-settings-status">{venue.status || "active"}</div>
                </div>

                {canToggle && (
                    <div className="border border-[#2a2a2a] p-4 flex items-start justify-between gap-4" data-testid="venue-settings-digest-card">
                        <div className="flex-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400] flex items-center gap-1"><Mail className="w-3 h-3" /> Weekly follower digest</div>
                            <div className="mt-1 font-display font-bold uppercase text-lg">Monday morning recap</div>
                            <p className="mt-1 text-xs text-[#a3a3a3]">A concise email with the past 7 days of follower growth, top sources, best-growth events, upcoming shows, and attendance patterns for this venue.</p>
                        </div>
                        <button
                            onClick={toggle}
                            disabled={saving || digestEnabled === null}
                            data-testid="venue-settings-digest-toggle"
                            aria-pressed={!!digestEnabled}
                            className={`pm-btn text-xs ${digestEnabled ? "pm-btn-primary" : ""}`}
                        >
                            {digestEnabled === null ? "…" : digestEnabled ? "On" : "Off"}
                        </button>
                    </div>
                )}

                <div className="border border-[#2a2a2a] p-4">
                    <div className="text-sm text-[#a3a3a3]">To edit your public venue page, go to <Link to={`/venue/${venue.id}/profile`} className="text-[#ff4400] hover:underline">Venue Page</Link>. Team access is managed under <Link to={`/venue/${venue.id}/team`} className="text-[#ff4400] hover:underline">Team</Link>. Contact a Platform Admin to suspend/restore this venue.</div>
                </div>
            </div>
        </div>
    );
}
